import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  Copy, ArrowRight, ShieldAlert, CheckCircle2, XCircle, Loader2,
  Database, ServerCog, Trash2, RefreshCw,
} from "lucide-react";

const POLL_MS = 2000;

// Human-readable order for the collection progress list
const COLLECTION_ORDER = [
  "users", "addresses", "products", "categories", "brands", "banners",
  "routes", "subscriptions", "deliveries", "wallets", "transactions",
  "tenant_wallets", "tenant_transactions", "bottles", "bottle_logs",
  "reels", "device_tokens", "push_logs", "notifications",
  "loyalty_ledger", "loyalty_settings", "referral_settings", "referrals",
];

function pct(a, b) {
  if (!b) return 0;
  return Math.min(100, Math.round((a / b) * 100));
}

export default function SuperMigrate() {
  const [step, setStep] = useState("form");     // form | preview | running | done | error
  const [sending, setSending] = useState(false);

  // Form
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourcePhone, setSourcePhone] = useState("");
  const [sourceOtp, setSourceOtp] = useState("");
  const [sourceSlug, setSourceSlug] = useState("");
  const [targetSlug, setTargetSlug] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpHint, setOtpHint] = useState("");

  // Preview + job state
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [wipeConfirm, setWipeConfirm] = useState(false);
  const pollRef = useRef(null);

  // Recent jobs
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    loadRecent();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadRecent = async () => {
    try {
      const { data } = await api.get("/api/super-admin/migration");
      setRecent(data.jobs || []);
    } catch { /* silent */ }
  };

  const canRequestOtp = sourceUrl.trim() && sourcePhone.trim() && !sending;
  const canPreview = sourceUrl.trim() && sourcePhone.trim()
    && sourceOtp.trim() && sourceSlug.trim() && !sending;

  const handleOtpRequest = async () => {
    if (!canRequestOtp) return;
    setSending(true);
    try {
      const { data } = await api.post("/api/super-admin/migration/otp-request", {
        source_backend_url: sourceUrl.trim(),
        source_super_admin_phone: sourcePhone.trim(),
      });
      setOtpSent(true);
      setOtpHint(data.otp_hint || "");
      toast.success(data.otp_hint ? `OTP sent (demo hint: ${data.otp_hint})` : "OTP sent to source super admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send OTP");
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async () => {
    if (!canPreview) return;
    setSending(true);
    try {
      const { data } = await api.post("/api/super-admin/migration/preview", {
        source_backend_url: sourceUrl.trim(),
        source_super_admin_phone: sourcePhone.trim(),
        source_otp: sourceOtp.trim(),
        source_tenant_slug: sourceSlug.trim().toLowerCase(),
        target_tenant_slug: targetSlug.trim().toLowerCase() || null,
      });
      setPreview(data);
      setStep("preview");
      setWipeConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setSending(false);
    }
  };

  const handleStart = async () => {
    if (preview.requires_confirm_wipe && !wipeConfirm) {
      toast.error("Please confirm you want to wipe the existing target tenant");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(
        `/api/super-admin/migration/${preview.migration_id}/start`,
        { confirm_wipe: preview.requires_confirm_wipe }
      );
      setStep("running");
      setJob({
        id: preview.migration_id,
        status: "running",
        phase: "starting",
        records_copied: 0,
        records_total: preview.source_counts
          ? Object.values(preview.source_counts).reduce((s, n) => s + Number(n || 0), 0)
          : 0,
        collections: Object.fromEntries(
          COLLECTION_ORDER.map((c) => [c, { copied: 0, total: Number(preview.source_counts?.[c] || 0) }])
        ),
        source_slug: preview.source_tenant?.slug,
        target_slug: preview.target_slug,
        error: null,
      });
      startPolling(preview.migration_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start migration");
    } finally {
      setSending(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/api/super-admin/migration/${jobId}`);
        setJob(data);
        if (data.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStep("done");
          toast.success("Migration completed");
          loadRecent();
        } else if (data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStep("error");
          toast.error("Migration failed — see details");
          loadRecent();
        }
      } catch { /* swallow, retry on next tick */ }
    }, POLL_MS);
  };

  const resetForm = () => {
    setStep("form");
    setPreview(null);
    setJob(null);
    setSourceOtp("");
    setOtpSent(false);
    setOtpHint("");
    setWipeConfirm(false);
  };

  // -------------------- Renders --------------------
  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="super-migrate-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-blue-100 text-blue-700"><Copy className="w-5 h-5"/></div>
        <div>
          <h1 className="text-2xl font-semibold">Migrate Tenant Across Deployments</h1>
          <p className="text-sm text-gray-500">Copy an entire tenant — users, orders, subscriptions, deliveries, wallets — from another running instance.</p>
        </div>
      </div>

      {step === "form" && <FormCard {...{
        sourceUrl, setSourceUrl, sourcePhone, setSourcePhone,
        sourceOtp, setSourceOtp, sourceSlug, setSourceSlug,
        targetSlug, setTargetSlug, otpSent, otpHint,
        canRequestOtp, canPreview, sending,
        handleOtpRequest, handlePreview,
      }}/>}

      {step === "preview" && preview && (
        <PreviewCard preview={preview} wipeConfirm={wipeConfirm}
                     setWipeConfirm={setWipeConfirm} sending={sending}
                     onCancel={resetForm} onStart={handleStart}/>
      )}

      {(step === "running" || step === "done" || step === "error") && job && (
        <ProgressCard job={job} onDone={resetForm}/>
      )}

      <RecentJobsList jobs={recent} onRefresh={loadRecent}/>
    </div>
  );
}


// ---------------- Form ----------------
function FormCard(props) {
  const {
    sourceUrl, setSourceUrl, sourcePhone, setSourcePhone,
    sourceOtp, setSourceOtp, sourceSlug, setSourceSlug,
    targetSlug, setTargetSlug, otpSent, otpHint,
    canRequestOtp, canPreview, sending,
    handleOtpRequest, handlePreview,
  } = props;

  return (
    <div className="bg-white border rounded-xl p-6 space-y-5" data-testid="migrate-form-card">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Source backend URL"
               hint="Full URL of the OTHER deployment. e.g. https://api.other-cluster.com">
          <Input value={sourceUrl}
                 onChange={(e) => setSourceUrl(e.target.value)}
                 placeholder="https://api.source-deployment.com"
                 data-testid="migrate-source-url"/>
        </Field>
        <Field label="Source super-admin phone"
               hint="Must be an existing super_admin on the source deployment.">
          <Input value={sourcePhone}
                 onChange={(e) => setSourcePhone(e.target.value)}
                 placeholder="+91xxxxxxxxxx"
                 data-testid="migrate-source-phone"/>
        </Field>
      </div>

      <div>
        <Button variant="outline" size="sm" onClick={handleOtpRequest}
                disabled={!canRequestOtp}
                data-testid="migrate-send-otp-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
          {otpSent ? "Resend OTP" : "Send OTP to source"}
        </Button>
        {otpHint && (
          <span className="ml-3 text-xs text-gray-500">
            (Demo phone — OTP is <code className="font-mono">{otpHint}</code>)
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Source OTP" hint="Enter the 6-digit code sent to the source super-admin's phone.">
          <Input value={sourceOtp}
                 onChange={(e) => setSourceOtp(e.target.value)}
                 placeholder="123456" maxLength={6}
                 disabled={!otpSent}
                 data-testid="migrate-source-otp"/>
        </Field>
        <Field label="Source tenant slug" hint="The slug of the tenant to copy FROM.">
          <Input value={sourceSlug}
                 onChange={(e) => setSourceSlug(e.target.value)}
                 placeholder="e.g. dairyfresh"
                 data-testid="migrate-source-slug"/>
        </Field>
        <Field label="Target tenant slug (optional)"
               hint="Leave blank to reuse the source slug.">
          <Input value={targetSlug}
                 onChange={(e) => setTargetSlug(e.target.value)}
                 placeholder="e.g. dairyfresh"
                 data-testid="migrate-target-slug"/>
        </Field>
      </div>

      <div className="flex justify-end">
        <Button onClick={handlePreview} disabled={!canPreview}
                data-testid="migrate-preview-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
          Continue
          <ArrowRight className="w-4 h-4 ml-2"/>
        </Button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}


// ---------------- Preview ----------------
function PreviewCard({ preview, wipeConfirm, setWipeConfirm, sending, onCancel, onStart }) {
  const totalRecords = Object.values(preview.source_counts || {})
    .reduce((s, n) => s + Number(n || 0), 0);
  return (
    <div className="bg-white border rounded-xl p-6 space-y-5" data-testid="migrate-preview-card">
      <div className="flex items-center gap-2">
        <ServerCog className="w-5 h-5 text-gray-500"/>
        <h2 className="font-semibold text-lg">Migration Preview</h2>
      </div>

      {/* Source */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="text-xs font-medium text-slate-500 uppercase mb-1">Source</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-lg font-semibold">{preview.source_tenant.name}</span>
          <span className="text-sm text-gray-500">/{preview.source_tenant.slug}</span>
          <span className="text-sm text-gray-500">· {totalRecords.toLocaleString()} records</span>
        </div>
      </div>

      {/* Target */}
      {preview.target_exists ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3"
             data-testid="migrate-preview-wipe-warning">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5"/>
            <div>
              <div className="font-semibold text-red-800">
                Target tenant &quot;/{preview.target_slug}&quot; already exists
              </div>
              <div className="text-sm text-red-700 mt-1">
                Proceeding will PERMANENTLY DELETE all existing data for this tenant
                and replace it with the source data. This cannot be undone.
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-700">
            Will delete (target): {" "}
            {Object.entries(preview.target_summary || {})
              .filter(([k, v]) => !k.startsWith("_") && Number(v) > 0)
              .map(([k, v]) => `${v} ${k}`)
              .join(" · ") || "no records"}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={wipeConfirm}
                   onChange={(e) => setWipeConfirm(e.target.checked)}
                   data-testid="migrate-wipe-confirm"/>
            <span>Yes, I understand — wipe the existing target and copy source data</span>
          </label>
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5"/>
            <div>
              <div className="font-semibold text-emerald-800">
                Target tenant &quot;/{preview.target_slug}&quot; does not exist
              </div>
              <div className="text-sm text-emerald-700 mt-1">
                A fresh tenant will be created with the source data.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Record counts */}
      <div>
        <div className="text-sm font-semibold mb-2">Records to copy</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
          {COLLECTION_ORDER.map((c) => (
            <div key={c} className="flex justify-between text-gray-700">
              <span>{c}</span>
              <span className="tabular-nums">{Number(preview.source_counts?.[c] || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onCancel} data-testid="migrate-cancel-btn">
          <XCircle className="w-4 h-4 mr-2"/> Cancel
        </Button>
        <Button onClick={onStart}
                disabled={sending || (preview.requires_confirm_wipe && !wipeConfirm)}
                data-testid="migrate-start-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Copy className="w-4 h-4 mr-2"/>}
          {preview.requires_confirm_wipe ? "Wipe target & migrate" : "Start migration"}
        </Button>
      </div>
    </div>
  );
}


// ---------------- Progress ----------------
function ProgressCard({ job, onDone }) {
  const recordsCopied = Number(job.records_copied || 0);
  const recordsTotal = Number(job.records_total || 0);
  const totalPct = pct(recordsCopied, recordsTotal);
  const collectionsList = COLLECTION_ORDER.map((c) => ({
    name: c,
    ...(job.collections?.[c] || { copied: 0, total: 0 }),
  }));
  const done = job.status === "completed";
  const err = job.status === "failed";

  return (
    <div className="bg-white border rounded-xl p-6 space-y-4" data-testid="migrate-progress-card">
      <div className="flex items-center gap-2">
        {done && <CheckCircle2 className="w-5 h-5 text-emerald-600"/>}
        {err && <XCircle className="w-5 h-5 text-red-600"/>}
        {!done && !err && <Loader2 className="w-5 h-5 animate-spin text-blue-600"/>}
        <h2 className="font-semibold text-lg">
          {done ? "Migration complete" : err ? "Migration failed" : "Migration in progress"}
        </h2>
        <span className="text-xs text-gray-500 ml-auto">
          {job.source_slug || "—"} → {job.target_slug || "—"}
        </span>
      </div>

      <div>
        <div className="flex justify-between text-sm text-gray-700 mb-1">
          <span>Phase: <span className="font-mono">{job.phase || "starting"}</span></span>
          <span>{recordsCopied.toLocaleString()} / {recordsTotal.toLocaleString()} ({totalPct}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div className={`h-3 transition-all ${err ? "bg-red-500" : "bg-blue-600"}`}
               style={{ width: `${totalPct}%` }}
               data-testid="migrate-progress-bar"/>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-sm max-h-80 overflow-y-auto">
        {collectionsList.map((c) => {
          const copied = Number(c.copied || 0);
          const total = Number(c.total || 0);
          const p = pct(copied, total);
          return (
            <div key={c.name} className="flex justify-between items-center py-1 border-b">
              <span className="text-gray-700">{c.name}</span>
              <span className="text-xs text-gray-500 tabular-nums">
                {copied.toLocaleString()} / {total.toLocaleString()}
                {" "}({p}%)
              </span>
            </div>
          );
        })}
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800"
             data-testid="migrate-error-detail">
          {job.error || "Unknown error"}
        </div>
      )}

      {(done || err) && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={onDone} data-testid="migrate-restart-btn">
            <RefreshCw className="w-4 h-4 mr-2"/> Start another migration
          </Button>
        </div>
      )}
    </div>
  );
}


// ---------------- Recent jobs ----------------
function RecentJobsList({ jobs, onRefresh }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <div className="bg-white border rounded-xl p-6" data-testid="migrate-recent-list">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-gray-500"/>
          <h3 className="font-semibold">Recent migrations</h3>
        </div>
        <button onClick={onRefresh} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                data-testid="migrate-refresh-recent-btn">
          <RefreshCw className="w-3 h-3"/> Refresh
        </button>
      </div>
      <div className="divide-y">
        {jobs.map((j) => (
          <div key={j.id} className="py-2 flex items-center justify-between text-sm">
            <div>
              <div className="font-mono text-xs text-gray-600 truncate">{j.source_backend_url}</div>
              <div className="text-gray-800">
                {j.source_slug} → {j.target_slug}
                <span className="text-gray-500 ml-2">
                  · {j.records_copied?.toLocaleString?.() || 0} records
                </span>
              </div>
            </div>
            <StatusPill status={j.status}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    completed: "bg-emerald-100 text-emerald-800",
    running: "bg-blue-100 text-blue-800",
    preview_ready: "bg-gray-100 text-gray-700",
    failed: "bg-red-100 text-red-800",
    aborted: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded ${styles[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}
